<template>
  <div class="pt-history-page">
    <header class="pt-history-page__header">
      <button class="pt-history-page__back" @click="goBack">←</button>
      <h2 class="pt-history-page__title">{{ $t ? $t('AI 통역 PT — 발표 이력') : 'AI 통역 PT — 발표 이력' }}</h2>
      <button class="pt-history-page__open" @click="openBroadcast">
        ▶ {{ $t ? $t('새 발표 시작') : '새 발표 시작' }}
      </button>
    </header>

    <div class="pt-history-page__body">
      <div v-if="loading" class="pt-history-page__state">{{ $t ? $t('불러오는 중…') : '불러오는 중…' }}</div>
      <div v-else-if="error" class="pt-history-page__state pt-history-page__state--err">{{ error }}</div>
      <div v-else-if="!items.length" class="pt-history-page__state">
        {{ $t ? $t('아직 발표 이력이 없습니다.') : '아직 발표 이력이 없습니다.' }}
      </div>

      <ul v-else class="pt-history-list">
        <li v-for="item in items" :key="item.session_id" class="pt-history-card" @click="openDetail(item)">
          <div class="pt-history-card__main">
            <div class="pt-history-card__title">
              <span v-if="!editingId || editingId !== item.session_id">{{ item.title || ($t ? $t('(제목 없음)') : '(제목 없음)') }}</span>
              <input
                v-else
                v-model="editingTitle"
                class="pt-history-card__title-input"
                @click.stop
                @keyup.enter="saveTitle(item)"
                @keyup.esc="cancelEdit"
              />
            </div>
            <div class="pt-history-card__meta">
              <span>{{ formatTime(item.started_at) }}</span>
              <span class="pt-history-card__dot">·</span>
              <span>{{ formatDuration(item) }}</span>
              <span class="pt-history-card__dot">·</span>
              <span>{{ item.turn_count }} {{ $t ? $t('발화') : '발화' }}</span>
              <span class="pt-history-card__dot">·</span>
              <span class="pt-history-card__mode" :class="'pt-history-card__mode--' + item.mode">
                {{ item.mode === 'live' ? 'LIVE' : 'STT' }}
              </span>
            </div>
            <div class="pt-history-card__langs">
              <span class="pt-history-card__source">{{ item.source_language }}</span>
              <span class="pt-history-card__arrow">→</span>
              <span
                v-for="lang in item.target_languages"
                :key="item.session_id + '_' + lang"
                class="pt-history-card__lang"
              >{{ lang }}</span>
            </div>
          </div>
          <div class="pt-history-card__actions" @click.stop>
            <button
              v-if="!editingId || editingId !== item.session_id"
              class="pt-history-card__btn"
              @click="startEdit(item)"
              :title="$t ? $t('제목 수정') : '제목 수정'"
            >✎</button>
            <template v-else>
              <button class="pt-history-card__btn pt-history-card__btn--primary" @click="saveTitle(item)">저장</button>
              <button class="pt-history-card__btn" @click="cancelEdit">취소</button>
            </template>
            <button
              class="pt-history-card__btn pt-history-card__btn--danger"
              @click="confirmDelete(item)"
              :title="$t ? $t('삭제') : '삭제'"
            >🗑</button>
          </div>
        </li>
      </ul>

      <!-- 페이지네이션 -->
      <div v-if="total > limit" class="pt-history-page__pager">
        <button class="pt-history-page__pager-btn" :disabled="offset <= 0" @click="prevPage">‹</button>
        <span class="pt-history-page__pager-info">{{ offset + 1 }} - {{ Math.min(offset + limit, total) }} / {{ total }}</span>
        <button class="pt-history-page__pager-btn" :disabled="offset + limit >= total" @click="nextPage">›</button>
      </div>
    </div>
  </div>
</template>

<script>
import { useVoicePtBroadcast } from '@/composables/useVoicePtBroadcast'
import * as UiUtil from '@/util/UiUtil'

export default {
  name: 'AiVoicePtHistory',
  data() {
    return {
      pt: null,
      items: [],
      total: 0,
      limit: 20,
      offset: 0,
      loading: false,
      error: null,
      editingId: null,
      editingTitle: '',
    }
  },
  computed: {
    projectId() { return this.$route.params.proj_id },
  },
  created() {
    this.pt = useVoicePtBroadcast()
    this.loadList()
  },
  methods: {
    async loadList() {
      this.loading = true
      this.error = null
      try {
        const data = await this.pt.fetchSessions({ limit: this.limit, offset: this.offset })
        if (data) {
          this.items = Array.isArray(data.items) ? data.items : []
          this.total = data.total ? data.total : 0
        }
      } catch (e) {
        this.error = (e && e.message) ? e.message : '이력 조회 실패'
      } finally {
        this.loading = false
      }
    },

    formatTime(iso) {
      if (!iso) return '-'
      try {
        const d = new Date(iso)
        const pad = function (n) { return n < 10 ? '0' + n : '' + n }
        return d.getFullYear() + '-' + pad(d.getMonth()+1) + '-' + pad(d.getDate())
          + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes())
      } catch (e) { return iso }
    },

    formatDuration(item) {
      let sec = 0
      if (item.total_audio_seconds && item.total_audio_seconds > 0) {
        sec = Math.round(item.total_audio_seconds)
      } else if (item.started_at && item.ended_at) {
        try {
          sec = Math.max(0, Math.round((new Date(item.ended_at).getTime() - new Date(item.started_at).getTime()) / 1000))
        } catch (e) { sec = 0 }
      }
      const m = Math.floor(sec / 60)
      const s = sec % 60
      if (m > 0) return m + '분 ' + s + '초'
      return s + '초'
    },

    openDetail(item) {
      this.$router.push({
        name: 'ai-voice-pt-history-detail',
        params: { proj_id: this.projectId, sessionId: item.session_id },
      })
    },
    openBroadcast() {
      this.$router.push('/project/' + this.projectId + '/ai-voice-pt')
    },
    goBack() {
      this.$router.push('/project/' + this.projectId + '/home').catch(() => {})
    },

    startEdit(item) {
      this.editingId = item.session_id
      this.editingTitle = item.title ? item.title : ''
    },
    cancelEdit() {
      this.editingId = null
      this.editingTitle = ''
    },
    async saveTitle(item) {
      const newTitle = (this.editingTitle || '').trim()
      if (!newTitle) { this.cancelEdit(); return }
      try {
        await this.pt.renameSession(item.session_id, newTitle)
        item.title = newTitle
      } catch (e) {
        if (this.$vs && this.$vs.notify) {
          this.$vs.notify({ title: 'PT', text: e.message, color: 'danger', position: 'top-center', time: 3000 })
        }
      }
      this.cancelEdit()
    },

    async confirmDelete(item) {
      if (!(await UiUtil.confirmDialog((this.$t ? this.$t('정말로 이 발표 이력을 삭제하시겠습니까?') : '정말로 이 발표 이력을 삭제하시겠습니까?')))) return
      try {
        await this.pt.deleteSession(item.session_id)
        // 목록에서 제거
        for (let i = 0; i < this.items.length; i++) {
          if (this.items[i].session_id === item.session_id) {
            this.items.splice(i, 1)
            this.total = Math.max(0, this.total - 1)
            break
          }
        }
      } catch (e) {
        if (this.$vs && this.$vs.notify) {
          this.$vs.notify({ title: 'PT', text: e.message, color: 'danger', position: 'top-center', time: 3000 })
        }
      }
    },

    prevPage() { this.offset = Math.max(0, this.offset - this.limit); this.loadList() },
    nextPage() { this.offset = this.offset + this.limit; this.loadList() },
  },
}
</script>

<style scoped>
.pt-history-page {
  min-height: 100vh;
  background: #F8FAFC;
}

.pt-history-page__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  background: #fff;
  border-bottom: 1px solid #E5E7EB;
  position: sticky;
  top: 0;
  z-index: 10;
}

.pt-history-page__back {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  font-size: 20px;
  cursor: pointer;
  color: #374151;
  border-radius: 8px;
}
.pt-history-page__back:hover { background: #F3F4F6; }

.pt-history-page__title {
  flex: 1;
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #111827;
}

.pt-history-page__open {
  padding: 8px 16px;
  border: none;
  background: #4338CA;
  color: white;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
}
.pt-history-page__open:hover { background: #3730A3; }

.pt-history-page__body {
  max-width: 920px;
  margin: 0 auto;
  padding: 24px;
}

.pt-history-page__state {
  padding: 48px 0;
  text-align: center;
  color: #6B7280;
}
.pt-history-page__state--err { color: #DC2626; }

.pt-history-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pt-history-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  cursor: pointer;
  transition: box-shadow 0.15s;
}
.pt-history-card:hover { box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06); }

.pt-history-card__main { flex: 1; min-width: 0; }

.pt-history-card__title {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pt-history-card__title-input {
  width: 100%;
  padding: 4px 8px;
  border: 1px solid #4338CA;
  border-radius: 4px;
  font-size: 14px;
}

.pt-history-card__meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #6B7280;
}
.pt-history-card__dot { color: #D1D5DB; }
.pt-history-card__mode {
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 3px;
  letter-spacing: 0.5px;
  font-weight: 700;
}
.pt-history-card__mode--live { background: #FEE2E2; color: #991B1B; }
.pt-history-card__mode--stt  { background: #DBEAFE; color: #1E40AF; }

.pt-history-card__langs {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  font-size: 11px;
}
.pt-history-card__source {
  background: #1F2937;
  color: #fff;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 600;
}
.pt-history-card__arrow { color: #9CA3AF; }
.pt-history-card__lang {
  background: #E5E7EB;
  color: #374151;
  padding: 2px 6px;
  border-radius: 4px;
}

.pt-history-card__actions {
  display: flex;
  gap: 6px;
}
.pt-history-card__btn {
  width: 32px;
  height: 32px;
  border: 1px solid #E5E7EB;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  color: #374151;
}
.pt-history-card__btn:hover { background: #F3F4F6; }
.pt-history-card__btn--danger:hover { background: #FEE2E2; border-color: #FCA5A5; color: #B91C1C; }
.pt-history-card__btn--primary {
  width: auto;
  padding: 0 12px;
  background: #4338CA;
  color: white;
  border-color: #4338CA;
}

.pt-history-page__pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 16px;
}
.pt-history-page__pager-btn {
  width: 36px;
  height: 36px;
  border: 1px solid #E5E7EB;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
}
.pt-history-page__pager-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.pt-history-page__pager-info { font-size: 13px; color: #6B7280; }
</style>
